import { useEffect, useState } from "react";
import BaseModal from "@components/modals/BaseModal";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import SearchAlgolia, { type SearchOption } from "@components/ui/search/SearchAlgolia";
import Icon1 from "@assets/icons/Icon";
import { searchOrdresFull } from "@services/algolia";
import { vehiclesApi } from "@services/api/vehicles";
import { showNotification } from "context/notification.service";
import { useDataStore } from "@store/user_org";
import { useVehiculesStore } from "@store/vehiculesStore";
import type { Vehicle, VehicleReservation } from "types/garage/vehicules";

/** Départ / retour par défaut : la journée ouvrée du garage. */
const DEFAULT_START_HOUR = 8;
const DEFAULT_END_HOUR = 17.5;

function hourToInput(h: number): string {
  const hh = Math.floor(h);
  return `${String(hh).padStart(2, "0")}:${String(Math.round((h - hh) * 60)).padStart(2, "0")}`;
}

function inputToHour(v: string): number {
  const [h, m] = v.split(":").map(Number);
  return (h || 0) + (m || 0) / 60;
}

function frDate(iso: string): string {
  return iso ? iso.split("-").reverse().join("/") : "—";
}

/**
 * Réserver un véhicule depuis le planning flotte : on clique la ligne du véhicule,
 * on cherche l'ordre, on pose les dates.
 *
 * L'ordre se cherche avec Algolia comme partout ailleurs, et une fois choisi la modale
 * montre ce qu'il a DÉJÀ comme véhicule : un ordre qui enchaîne un prêt puis une
 * location est normal, deux véhicules sur le même créneau ne l'est pas — autant le voir
 * avant de confirmer plutôt que de le découvrir sur le planning.
 */
export default function CreateReservationModal({
  vehicle,
  defaultDate,
  onClose,
  onCreated,
}: {
  vehicle: Vehicle;
  /** Jour cliqué dans la grille — sert de date de départ. */
  defaultDate: string;
  onClose: () => void;
  onCreated?: (reservation: VehicleReservation) => void;
}) {
  const orgId = useDataStore((s) => s.orgData?.id);
  const vehicles = useVehiculesStore((s) => s.vehicles);

  const [query, setQuery] = useState("");
  const [existing, setExisting] = useState<VehicleReservation[]>([]);
  const [available, setAvailable] = useState<boolean | null>(null);
  const [ordre, setOrdre] = useState<SearchOption | null>(null);
  const [startDate, setStartDate] = useState(defaultDate);
  const [endDate, setEndDate] = useState(defaultDate);
  const [startHour, setStartHour] = useState(DEFAULT_START_HOUR);
  const [endHour, setEndHour] = useState(DEFAULT_END_HOUR);
  const [saving, setSaving] = useState(false);

  // Toutes les mobilités de l'ordre, pas seulement celle que l'ordre reflète : un prêt
  // puis une location qui se suivent sont deux réservations.
  useEffect(() => {
    if (!orgId || !ordre) { setExisting([]); return; }
    let alive = true;
    vehiclesApi
      .listReservations({ org_id: orgId, ordre_id: ordre.value })
      .then((rs) => { if (alive) setExisting(rs); })
      .catch(() => { if (alive) setExisting([]); });
    return () => { alive = false; };
  }, [orgId, ordre]);

  // Disponibilité RÉELLE sur la période : les prêts synchronisés depuis ServiceBox ne
  // sont pas tous dans la grille qu'on a sous les yeux, un test local mentirait.
  useEffect(() => {
    if (!orgId || !startDate || !endDate || endDate < startDate) { setAvailable(null); return; }
    let alive = true;
    vehiclesApi
      .available({
        org_id: orgId, start_date: startDate, end_date: endDate,
        start_hour: startHour, end_hour: endHour, all_categories: true,
      })
      .then((free) => { if (alive) setAvailable(free.some((v) => v.id === vehicle.id)); })
      .catch(() => { if (alive) setAvailable(null); });
    return () => { alive = false; };
  }, [orgId, vehicle.id, startDate, endDate, startHour, endHour]);

  const datesInvalid = !startDate || !endDate || endDate < startDate;
  const sameDayInvalid = !datesInvalid && startDate === endDate && endHour <= startHour;

  const submit = async () => {
    if (!orgId || !ordre || datesInvalid || sameDayInvalid) return;
    setSaving(true);
    try {
      const created = await vehiclesApi.createReservation({
        org_id: orgId,
        vehicleId: vehicle.id,
        // Le nom affiché sur le planning vient de l'ordre : « OR-123 — DUPONT ».
        clientName: (ordre.label || "").split(" — ").slice(1).join(" — ") || ordre.label || "",
        startDate,
        endDate,
        hasPickup: true,
        hasReturn: true,
        startHour,
        returnHour: endHour,
        durationHours: startDate === endDate ? Math.max(0.25, endHour - startHour) : undefined,
        ordreId: ordre.value,
      } as Omit<VehicleReservation, "id"> & { org_id: string });
      showNotification({ type: "success", message: "Réservation créée" });
      onCreated?.(created);
      onClose();
    } catch {
      showNotification({ type: "error", message: "Erreur lors de la création de la réservation" });
    }
    setSaving(false);
  };

  const plate = vehicle.plate || "—";
  const label = [vehicle.marque, vehicle.modele].filter(Boolean).join(" ");

  return (
    <BaseModal
      onClose={onClose}
      title={`Réserver ${plate}`}
      startIcon={<Icon1 name="car" size={20} />}
      width="max-w-xl"
    >
      <div className="flex flex-col gap-4 p-4">
        <div className="flex flex-row items-center gap-3 rounded-lg border border-neutral-200 px-3 py-2">
          <Icon1 name="car" size={18} color="#6366f1" />
          <div className="flex flex-col">
            <span className="text-h3 font-medium">{plate}</span>
            <span className="text-h4 text-neutral-400">{label || "Véhicule de la flotte"}</span>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-h4 font-medium text-neutral-600">Ordre</span>
          {ordre ? (
            <div className="flex flex-row items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2">
              <div className="flex-1 flex flex-col min-w-0">
                <span className="text-h3 font-medium truncate">{ordre.label}</span>
                <span className="text-h4 text-neutral-500 truncate">
                  {[ordre.extra_label, ordre.extra_label_2].filter(Boolean).join(" · ") || "—"}
                </span>
              </div>
              <SecondaryButton
                variant="small"
                onClick={() => {
                  setOrdre(null);
                  setQuery("");
                }}
              >
                Changer
              </SecondaryButton>
            </div>
          ) : (
            <SearchAlgolia
              value={query}
              setValue={setQuery}
              placeholder="Rechercher un ordre (n°, client, plaque)…"
              classNameCustom="w-full"
              focus_on_start
              onSearch={async (q) => (orgId ? searchOrdresFull(q, orgId) : [])}
              onAction={(option) => {
                if (typeof option !== "string") setOrdre(option);
                return true;
              }}
            />
          )}
        </div>

        {ordre && existing.length > 0 && (
          <div
            className="flex flex-col gap-1 rounded-lg px-3 py-2"
            style={{ background: "#FFFBEB", border: "1px solid #FDE68A" }}
          >
            <span className="text-h4 font-medium" style={{ color: "#92400E" }}>
              Cet ordre a déjà{" "}
              {existing.length === 1 ? "un véhicule attribué" : `${existing.length} véhicules attribués`}
            </span>
            {existing.map((r) => {
              const v = vehicles.find((x) => x.id === r.vehicleId);
              return (
                <span key={r.id} className="text-h4" style={{ color: "#7C2D12" }}>
                  {v?.plate || "véhicule inconnu"}
                  {v?.modele ? ` (${v.modele})` : ""} — du {frDate(r.startDate)} au {frDate(r.endDate)}
                </span>
              );
            })}
          </div>
        )}

        <div className="flex flex-row gap-3">
          <div className="flex-1 flex flex-col gap-1">
            <span className="text-h4 font-medium text-neutral-600">Départ</span>
            <div className="flex flex-row gap-2">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="flex-1 text-h3 rounded-lg border border-neutral-200 px-2 py-1.5"
              />
              <input
                type="time"
                value={hourToInput(startHour)}
                step={900}
                onChange={(e) => setStartHour(inputToHour(e.target.value))}
                className="text-h3 rounded-lg border border-neutral-200 px-2 py-1.5"
              />
            </div>
          </div>
          <div className="flex-1 flex flex-col gap-1">
            <span className="text-h4 font-medium text-neutral-600">Retour</span>
            <div className="flex flex-row gap-2">
              <input
                type="date"
                value={endDate}
                min={startDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="flex-1 text-h3 rounded-lg border border-neutral-200 px-2 py-1.5"
              />
              <input
                type="time"
                value={hourToInput(endHour)}
                step={900}
                onChange={(e) => setEndHour(inputToHour(e.target.value))}
                className="text-h3 rounded-lg border border-neutral-200 px-2 py-1.5"
              />
            </div>
          </div>
        </div>

        {datesInvalid && (
          <span className="text-h4 text-red-500">Le retour doit être au plus tôt le jour du départ.</span>
        )}
        {sameDayInvalid && (
          <span className="text-h4 text-red-500">
            Sur une même journée, l&apos;heure de retour doit suivre celle du départ.
          </span>
        )}
        {available === false && !datesInvalid && (
          <span className="text-h4 text-orange-600">
            ⚠ {plate} n&apos;est pas libre sur cette période — une autre réservation, ou un prêt
            venu de ServiceBox, la couvre déjà.
          </span>
        )}

        <div className="flex flex-row justify-end gap-2 pt-1">
          <SecondaryButton onClick={onClose}>Annuler</SecondaryButton>
          <Button onClick={submit} disabled={!ordre || datesInvalid || sameDayInvalid || saving}>
            {saving ? "Création…" : "Confirmer"}
          </Button>
        </div>
      </div>
    </BaseModal>
  );
}
